import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { BookingPackageType, BookingProductType } from '@products/models/booking.model';
import { PackageBookingService } from '@products/services/package-booking.service';
import { ProductBookingService } from '@products/services/product-booking.service';
import { BOOKING_DEBOUNCE_TIME } from '@shared/tokens';
import { PackageProductBookingActions } from '@store/products/booking/product/package/package-booking.actions';
import { ProductBookingActions } from '@store/products/booking/product/product-booking.actions';
import { PackageProductActions } from '@store/products/product.actions';
import { isEmpty, isNil } from 'lodash';
import { catchError, debounceTime, EMPTY, map, of, switchMap } from 'rxjs';

export const setPackageBooking$ = createEffect(
  (
    actions$ = inject(Actions),
    bookingDebounceTime = inject(BOOKING_DEBOUNCE_TIME, { optional: true }),
    productBookingService = inject(ProductBookingService)
  ) =>
    actions$.pipe(
      ofType(PackageProductActions.setPackageBooking),
      debounceTime(bookingDebounceTime),
      switchMap(({ bookingProductTypes }) => {
        if (isEmpty(bookingProductTypes)) {
          return EMPTY;
        }

        return productBookingService.setProductBookings$(bookingProductTypes, true).pipe(
          switchMap((postProductBookings) => {
            return productBookingService.postProductBookings$(postProductBookings).pipe(
              map((postProductBookingsResponse) => {
                const products = productBookingService.mapPostProductBookingsResponseToBookingPackageTypes(
                  bookingProductTypes,
                  postProductBookingsResponse
                );

                return PackageProductBookingActions.setPackageProductInBookingList(products);
              })
            );
          }),
          catchError(() => of(PackageProductBookingActions.setPackageProductInBookingList([])))
        );
      })
    ),
  { functional: true }
);

export const removePackageBooking$ = createEffect(
  (
    actions$ = inject(Actions),
    bookingDebounceTime = inject(BOOKING_DEBOUNCE_TIME, { optional: true }),
    productBookingService = inject(ProductBookingService),
    packageBookingService = inject(PackageBookingService)
  ) =>
    actions$.pipe(
      ofType(PackageProductActions.removePackageBooking),
      debounceTime(bookingDebounceTime),
      switchMap(({ packageNumber, packageIndexes }) => {
        if (isEmpty(packageIndexes)) {
          return EMPTY;
        }

        return packageBookingService.removePackageBookings$({ packageNumber, packageIndexes }).pipe(
          switchMap(({ bookedPackageTypes, removePostProductBookings }) => {
            return productBookingService.postProductBookings$(removePostProductBookings).pipe(
              map((postProductBookingsResponse) => {
                const products: BookingPackageType[] =
                  productBookingService.mapPostProductBookingsResponseToBookingPackageTypes(
                    bookedPackageTypes,
                    postProductBookingsResponse
                  );

                return PackageProductBookingActions.removePackageFromBookingList(products);
              })
            );
          }),
          catchError(() => of(PackageProductBookingActions.removePackageFromBookingList([])))
        );
      })
    ),
  { functional: true }
);

export const removePackageTariffBooking$ = createEffect(
  (
    actions$ = inject(Actions),
    bookingDebounceTime = inject(BOOKING_DEBOUNCE_TIME, { optional: true }),
    productBookingService = inject(ProductBookingService),
    packageBookingService = inject(PackageBookingService)
  ) =>
    actions$.pipe(
      ofType(PackageProductActions.removePackageTariffBooking),
      debounceTime(bookingDebounceTime),
      switchMap(({ packageNumber, packageIndex, ticketTypeId, ticketPersonId }) => {
        if (isNil(packageNumber) || isNil(packageIndex) || isNil(ticketTypeId) || isNil(ticketPersonId)) {
          return EMPTY;
        }

        return packageBookingService
          .removePackageTariffBookings$({ packageNumber, packageIndex, ticketTypeId, ticketPersonId })
          .pipe(
            switchMap(({ bookedPackageTypes, removePostProductBookings }) => {
              return productBookingService.postProductBookings$(removePostProductBookings).pipe(
                map((postProductBookingsResponse) => {
                  const products: BookingProductType[] =
                    productBookingService.mapPostProductBookingsResponseToBookingProductTypes(
                      bookedPackageTypes,
                      postProductBookingsResponse
                    );

                  return ProductBookingActions.removeProductFromBookingList(products);
                })
              );
            }),
            catchError(() => of(ProductBookingActions.removeProductFromBookingList([])))
          );
      })
    ),
  { functional: true }
);
